import { type FC, useCallback, useMemo, useState } from "react";

import { type MylensesSubmissionDetailsSource } from "@snapchat/blizzard-schema";
import { AddIcon, PencilIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId, MyLensesLensCtaText, MyLensesLensCtaType } from "../../gql-generated/types";
import { LensPreviewToBeReviewed } from "../../lens-preview/components/messages.generated";
import { Flexbox, FormModal } from "../../shared/components";
import { type LensPreviewMediaItem } from "../../shared/state";
import { LensDetailsPanel } from "../../submit/components/lens-details/shared/LensDetailsPanel";
import { useLensCtaForm } from "../hooks";
import { LensCtaTextLabelMap, makeLensCtaForUI } from "../models";
import { EditLensCtaFormSchema, type EditLensCtaFormValues } from "../validation";

import { LensCtaChild, LensCtaFormHeading, LensCtaFormInfo, formContainerStyle } from "./LensCta.styles";
import { LensCtaDisplay } from "./LensCtaDisplay";
import { EditLensCtaForm, EditLensCtaFormik, LensCtaFormFooter } from "./LensCtaForm";
import { LensCtaMediaPreview } from "./LensCtaMediaPreview";
import { messages } from "./messages.generated";

interface LensCtaProps {
    lensPreview: LensPreviewMediaItem;
    lensIconURL: string;
    label: string;
    createChangeDetailsEvent: (source: MylensesSubmissionDetailsSource) => () => void;
    lensOrSubmissionId: string;
    tooltipContent?: string;
}

export const LensCtaUI: FC<LensCtaProps> = ({
    lensPreview,
    lensIconURL,
    label,
    tooltipContent,
    createChangeDetailsEvent,
    lensOrSubmissionId,
}) => {
    const intl = useIntl();

    const { lensCta, lensCtaModalProps, applyLensCta, showLensCtaModal } = useLensCtaForm({
        createChangeDetailsEvent,
        lensOrSubmissionId,
    });

    const [showErrors, setShowErrors] = useState(false);

    const initialValues: EditLensCtaFormValues = useMemo(
        () =>
            makeLensCtaForUI({
                type: lensCta?.type ?? MyLensesLensCtaType.CtaTypeUnset,
                text: lensCta?.text ?? MyLensesLensCtaText.CtaTextUnset,
                website: {
                    url: lensCta?.website?.url ?? "",
                },
                deeplink: {
                    uri: lensCta?.deeplink?.uri ?? "",
                    appName: lensCta?.deeplink?.appName ?? "",
                    iosAppId: lensCta?.deeplink?.iosAppId ?? 0,
                    androidAppUrl: lensCta?.deeplink?.androidAppUrl ?? "",
                },
            }),
        [lensCta],
    );

    const handleFormikSubmit = useCallback(
        (values: EditLensCtaFormValues) => {
            const { type, text, deeplink, website } = EditLensCtaFormSchema.cast(values);
            if (type === MyLensesLensCtaType.CtaTypeDeeplink) {
                const { uri, appName, iosAppId, androidAppUrl } = deeplink;
                applyLensCta({
                    type,
                    text,
                    deeplink: {
                        uri: uri ?? "",
                        appName: appName ?? "",
                        iosAppId: iosAppId ?? 0,
                        androidAppUrl: androidAppUrl ?? "",
                    },
                });
            } else if (type === MyLensesLensCtaType.CtaTypeWebsite) {
                const { url } = website;
                applyLensCta({
                    type,
                    text,
                    website: {
                        url: url ?? "",
                    },
                });
            } else {
                applyLensCta({
                    type: MyLensesLensCtaType.CtaTypeUnset,
                    text: MyLensesLensCtaText.CtaTextMore,
                });
            }
        },
        [applyLensCta],
    );

    const { configs } = useFeatureGates();

    if (!configs[CofConfigId.MyLensesCta].enabled) {
        return null;
    }

    const lensCtaActionButtonText = intl.formatMessage(
        lensCta && lensCta.type !== MyLensesLensCtaType.CtaTypeUnset
            ? messages.lensCtaEditLabel
            : messages.lensCtaAddLabel,
        {
            label,
        },
    );
    const lensCtaIcon = lensCta && lensCta.type !== MyLensesLensCtaType.CtaTypeUnset ? <PencilIcon /> : <AddIcon />;

    return (
        <>
            <div data-test-id="lens-cta-container">
                <LensDetailsPanel
                    label={label}
                    tooltipContent={tooltipContent}
                    buttonIcon={lensCtaIcon}
                    buttonLabel={lensCtaActionButtonText}
                    onButtonClick={showLensCtaModal}
                    detailsDisplayContent={!!lensCta && <LensCtaDisplay lensCta={lensCta} />}
                    showDetailsDisplayContent={!!lensCta && lensCta.type !== MyLensesLensCtaType.CtaTypeUnset}
                />
            </div>

            <EditLensCtaFormik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleFormikSubmit}
                validationSchema={EditLensCtaFormSchema}
                validateOnMount={true}
            >
                {({ submitForm, values, isValid, setTouched }) => (
                    <FormModal
                        minHeight={400}
                        modalProps={{ ...lensCtaModalProps, width: 630, centered: false }}
                        footer={
                            <LensCtaFormFooter
                                isDisabled={!isValid}
                                onCancel={() => {
                                    setShowErrors(false);
                                    lensCtaModalProps.onCancel();
                                }}
                                onSave={() => {
                                    if (isValid) {
                                        setShowErrors(false);
                                        void submitForm();
                                    } else {
                                        setShowErrors(values.type !== MyLensesLensCtaType.CtaTypeUnset);
                                    }

                                    void setTouched({});
                                }}
                            />
                        }
                    >
                        <LensCtaFormHeading>{label}</LensCtaFormHeading>

                        <LensCtaFormInfo>
                            {tooltipContent}
                            {/** TODO Add Learn More Link */}
                        </LensCtaFormInfo>
                        <Flexbox justifyContent="space-between" className={formContainerStyle}>
                            <LensCtaChild>
                                <LensCtaMediaPreview
                                    lensPreview={lensPreview}
                                    lensCtaText={
                                        values.type === MyLensesLensCtaType.CtaTypeUnset
                                            ? ""
                                            : LensCtaTextLabelMap[values.text]
                                    }
                                    badgeText={<LensPreviewToBeReviewed />}
                                    lensIconURL={lensIconURL}
                                />
                            </LensCtaChild>
                            <LensCtaChild grow={1}>
                                <EditLensCtaForm showErrors={showErrors} setShowErrors={setShowErrors} />
                            </LensCtaChild>
                        </Flexbox>
                    </FormModal>
                )}
            </EditLensCtaFormik>
        </>
    );
};
